//This file is the first one when creating the bundled build and
//initializes some global namespaces and other global variables.
//Once the code is fully modular, the creation of the global namespace
//can/should be moved to the module-suffix instead.

import { getGlobal } from './global';

/**
 * Create a nested namespace on the global object. This will walk the dot-separated namespace path and ensure each
 * level exists as an object.
 *
 * @param {string} namespacePath - Dot-separated namespace path, e.g., 'Autodesk.Viewing'
 * @return {Object} The final namespace object, e.g., `global.Autodesk.Viewing`
 */
export function AutodeskNamespace(namespacePath) {
  var namespace = getGlobal();

  var parts = namespacePath.split('.');
  for (var i = 0; i < parts.length; ++i) {
    namespace[parts[i]] ??= {};
    namespace = namespace[parts[i]];
  }
  return namespace;
}

const Autodesk = AutodeskNamespace('Autodesk');
export default { Autodesk };

AutodeskNamespace('Autodesk.Viewing.Extensions');
AutodeskNamespace('Autodesk.Viewing.Math');
AutodeskNamespace('Autodesk.Viewing.Private');
AutodeskNamespace('Autodesk.Viewing.Scene');
AutodeskNamespace('Autodesk.Viewing.UI');

AutodeskNamespace('Autodesk.Extensions'); // Webpack bundled extensions.

AutodeskNamespace('Autodesk.LMVTK');

getGlobal().AutodeskNamespace = AutodeskNamespace;

// We initialize this flag already here in the module-prefix file to make sure
// it is set before any other initialization code is executed.
getGlobal().ENABLE_DEBUG = globalThis.ENABLE_DEBUG ?? globalThis.Autodesk?.Viewing?.Private?.ENABLE_DEBUG ?? false;


const reportedMissingSetters = new Set(); // Helper set for deduplicating missing-setter logs

/**
 * Export a single property from a source module to a target object. If the value is an object/function it is assigned
 * directly. Otherwise a getter is defined that returns the live value from the source module and a setter forwards to
 * sourceModule['set' + propertyName] when present.
 *
 * @param {Object} targetObject - Object to receive the exported property
 * @param {Object} sourceModule - Module containing the source property
 * @param {string} propertyName - Name of the property to export
 */
function exportSymbol(targetObject, sourceModule, propertyName) {
  if (targetObject == null) return;

  const value = sourceModule[propertyName];
  if (value !== null && (typeof value === 'object' || typeof value === 'function')) {
    targetObject[propertyName] = value;
    return;
  }

  Object.defineProperty(targetObject, propertyName, {
    get() {
      return sourceModule[propertyName];
    },
    set(newValue) {
      const setter = sourceModule['set' + propertyName];
      if (typeof setter === 'function') {
        setter.call(sourceModule, newValue);
      } else if (!reportedMissingSetters.has(propertyName)) {
        console.warn('No setter for global variable ' + propertyName + ' defined. It is read only.');
        reportedMissingSetters.add(propertyName);
      }
    },
    enumerable: true,
    configurable: true
  });
}

/**
 * This auxiliary function can be used to re-export a module's symbols onto the package-level `exports` and into a
 * short namespace, e.g., 'avm'. It is used from `src/index.js` to expose `Viewing.Private`, `Viewing.Math`, etc., as
 * `exports.avp` and `exports.avm` resp., and to maintain compatibility with legacy namespaces and Node.js builds.
 *
 * Example:
 *   // Re-export Viewing.Math into package exports and 'avm' namespace
 *   adsk_export(exports, Viewing.Math, 'avm');
 *
 * @param {Object} exports - The target export object to receive symbols, e.g., the package-level exports
 * @param {Object} sourceModule - The module whose properties will be exported
 * @param {string} [namespace] - Optional namespace key in `exports` to also export into, e.g., 'avp'
 * @param {string[]} [skip] - Optional array of property names to skip
 */
export function adsk_export(exports, sourceModule, namespace, skip) {
  const skipSet = Array.isArray(skip) ? new Set(skip) : null;

  for (let property in sourceModule) {
    if (skipSet && skipSet.has(property)) continue;

    const isOwnProperty = Object.prototype.hasOwnProperty.call(sourceModule, property);
    if (!isOwnProperty) continue;

    exportSymbol(exports, sourceModule, property);
    if (namespace) {
      exports[namespace] ??= {};
      exportSymbol(exports[namespace], sourceModule, property);
    }
  }
}